/** Режим смешивания для прохода частиц (аналог Unity: Additive vs Alpha). */
export type ParticleBlendMode = "additive" | "alpha";

/** Визуальный пресет: огонь / дым / искры (ветки во fragment shader: kind 0/1/2). */
export type ParticleVisualKind = "fire" | "smoke" | "spark";

/** Ключевая точка кривой по нормализованному возрасту t ∈ [0,1]. */
export interface CurvePoint {
  t: number;
  v: number;
}

/** Линейная интерполяция по ключам (как минимальный Curve в Unity). */
export function evalCurve(points: CurvePoint[], t: number): number {
  if (points.length === 0) return 1;
  const tt = Math.min(1, Math.max(0, t));
  if (tt <= points[0].t) return points[0].v;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (tt <= b.t) {
      const span = b.t - a.t;
      const u = span > 1e-8 ? (tt - a.t) / span : 0;
      return a.v + (b.v - a.v) * u;
    }
  }
  return points[points.length - 1].v;
}

/** Конфигурация эмиттера (spawn + движение + масштаб по времени жизни). */
export interface EmitterConfig {
  visual: ParticleVisualKind;
  blend: ParticleBlendMode;
  /** Частиц в секунду (устойчивый поток). */
  rate: number;
  /** Одноразовый выброс при старте (опционально). */
  burst?: number;
  lifetimeMin: number;
  lifetimeMax: number;
  /** Начальная скорость в м/с (направление + разброс). */
  speedMin: number;
  speedMax: number;
  /** Конус вокруг +Z мира (половина угла в радианах). */
  coneAngleRad: number;
  /** Базовый направление эмиссии (нормализовать снаружи при необходимости). По умолчанию вверх. */
  emitDir: [number, number, number];
  /** Гравитация по Z (м/с²); для дыма часто 0 или слегка вверх для «плавучести». */
  gravityZ: number;
  /** Линейное сопротивление скорости в секунду (0..1 за dt через exp). */
  drag: number;
  /** Амплитуда турбулентности (случайное ускорение в м/с² за кадр ~ шум). */
  turbulence: number;
  /** Масштаб размера квада по возрасту. */
  sizeOverLife: CurvePoint[];
  /** Множитель непрозрачности по возрасту (умножается на базовую альфу пресета). */
  alphaOverLife: CurvePoint[];
  /** Базовый диаметр квада в метрах (до кривой). */
  baseSize: number;
  /** Смещение центра эмиттера от якорной точки. */
  offset: [number, number, number];
  enabled: boolean;
}

/** Одна живая частица (CPU-симуляция). */
export interface ParticleCpu {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  born: number;
  lifetime: number;
  seed: number;
  emitterId: number;
}

/** Публичное состояние эмиттера: якорь в мире обновляется из игры. */
export interface EmitterAnchor {
  x: number;
  y: number;
  z: number;
}

/** Лимит буфера инстансов в particleRenderer (один draw на режим blend × visual или объединённый шейдер). */
export const PARTICLE_MAX_INSTANCES = 8192;

/** Floats на одну частицу: 4 vec4 = pos+size | age+t+seed+kind | time+born+life+pad | vx+vy+vz+pad */
export const PARTICLE_INSTANCE_FLOATS = 16;

export function presetFireEmitter(): EmitterConfig {
  return {
    visual: "fire",
    blend: "additive",
    rate: 48,
    burst: 12,
    lifetimeMin: 0.35,
    lifetimeMax: 0.65,
    speedMin: 0.45,
    speedMax: 1.35,
    coneAngleRad: 0.55,
    emitDir: [0, 0, 1],
    gravityZ: 0.35,
    drag: 1.8,
    turbulence: 2.2,
    baseSize: 0.42,
    sizeOverLife: [
      { t: 0, v: 0.35 },
      { t: 0.25, v: 1 },
      { t: 1, v: 0.15 },
    ],
    alphaOverLife: [
      { t: 0, v: 1 },
      { t: 0.85, v: 1 },
      { t: 1, v: 0 },
    ],
    offset: [0, 0, 0],
    enabled: true,
  };
}

export function presetSmokeEmitter(): EmitterConfig {
  return {
    visual: "smoke",
    blend: "alpha",
    rate: 22,
    burst: 6,
    lifetimeMin: 1.8,
    lifetimeMax: 3.2,
    speedMin: 0.12,
    speedMax: 0.42,
    coneAngleRad: 0.85,
    emitDir: [0, 0, 1],
    gravityZ: 0.08,
    drag: 0.65,
    turbulence: 0.85,
    baseSize: 0.55,
    sizeOverLife: [
      { t: 0, v: 0.25 },
      { t: 0.5, v: 1 },
      { t: 1, v: 1.65 },
    ],
    alphaOverLife: [
      { t: 0, v: 0 },
      { t: 0.12, v: 0.55 },
      { t: 0.65, v: 0.42 },
      { t: 1, v: 0 },
    ],
    offset: [0, 0, 0.05],
    enabled: true,
  };
}

/** Рваный огонь при снятии гладкой оболочки тела (ручной burst, emitters выключены). */
export function presetShellStripFire(): EmitterConfig {
  return {
    visual: "fire",
    blend: "additive",
    rate: 0,
    lifetimeMin: 0.22,
    lifetimeMax: 0.62,
    speedMin: 1.2,
    speedMax: 3.8,
    coneAngleRad: 1.05,
    emitDir: [0, 0, 1],
    gravityZ: 0.55,
    drag: 2.4,
    turbulence: 3.5,
    baseSize: 0.62,
    sizeOverLife: [
      { t: 0, v: 0.5 },
      { t: 0.2, v: 1 },
      { t: 1, v: 0.12 },
    ],
    alphaOverLife: [
      { t: 0, v: 1 },
      { t: 0.75, v: 1 },
      { t: 1, v: 0 },
    ],
    offset: [0, 0, 0],
    enabled: false,
  };
}

/** Дым при снятии оболочки — плотнее обычного декоративного дыма. */
export function presetShellStripSmoke(): EmitterConfig {
  return {
    visual: "smoke",
    blend: "alpha",
    rate: 0,
    lifetimeMin: 1.65,
    lifetimeMax: 3.0,
    speedMin: 0.25,
    speedMax: 0.95,
    coneAngleRad: 1.12,
    emitDir: [0, 0, 1],
    gravityZ: 0.05,
    drag: 0.55,
    turbulence: 1.25,
    baseSize: 0.62,
    sizeOverLife: [
      { t: 0, v: 0.35 },
      { t: 0.45, v: 1 },
      { t: 1, v: 1.75 },
    ],
    alphaOverLife: [
      { t: 0, v: 0.12 },
      { t: 0.08, v: 0.72 },
      { t: 0.55, v: 0.55 },
      { t: 1, v: 0 },
    ],
    offset: [0, 0, 0],
    enabled: false,
  };
}

/** Лёгкий дым от попадания пули в стену/пол/потолок (ручной burst). */
export function presetWallImpactSmoke(): EmitterConfig {
  return {
    visual: "smoke",
    blend: "alpha",
    rate: 0,
    lifetimeMin: 0.75,
    lifetimeMax: 2.05,
    speedMin: 0.28,
    speedMax: 1.05,
    coneAngleRad: 1.22,
    emitDir: [0, 0, 1],
    gravityZ: 0.1,
    drag: 0.68,
    turbulence: 1.35,
    baseSize: 0.36,
    sizeOverLife: [
      { t: 0, v: 0.22 },
      { t: 0.45, v: 1 },
      { t: 1, v: 1.55 },
    ],
    alphaOverLife: [
      { t: 0, v: 0 },
      { t: 0.1, v: 0.48 },
      { t: 0.6, v: 0.38 },
      { t: 1, v: 0 },
    ],
    offset: [0, 0, 0],
    enabled: false,
  };
}

/** Огонь при устойчивой стрельбе в одну точку на геометрии (>5 попаданий в бакет). */
export function presetWallSustainFire(): EmitterConfig {
  return {
    visual: "fire",
    blend: "additive",
    rate: 0,
    lifetimeMin: 0.18,
    lifetimeMax: 0.58,
    speedMin: 0.85,
    speedMax: 3.4,
    coneAngleRad: 1.08,
    emitDir: [0, 0, 1],
    gravityZ: 0.42,
    drag: 2.2,
    turbulence: 3.1,
    baseSize: 0.52,
    sizeOverLife: [
      { t: 0, v: 0.45 },
      { t: 0.22, v: 1 },
      { t: 1, v: 0.1 },
    ],
    alphaOverLife: [
      { t: 0, v: 1 },
      { t: 0.72, v: 1 },
      { t: 1, v: 0 },
    ],
    offset: [0, 0, 0],
    enabled: false,
  };
}

/** Короткие искры при попадании в геометрию / разрушении куба (burst). */
export function presetImpactSparks(): EmitterConfig {
  return {
    visual: "spark",
    blend: "additive",
    rate: 0,
    lifetimeMin: 0.14,
    lifetimeMax: 0.38,
    speedMin: 2.0,
    speedMax: 8.5,
    coneAngleRad: 0.95,
    emitDir: [0, 0, 1],
    gravityZ: 12.0,
    drag: 1.2,
    turbulence: 0.35,
    baseSize: 0.085,
    sizeOverLife: [
      { t: 0, v: 1 },
      { t: 0.35, v: 1 },
      { t: 1, v: 0.15 },
    ],
    alphaOverLife: [
      { t: 0, v: 1 },
      { t: 0.8, v: 1 },
      { t: 1, v: 0 },
    ],
    offset: [0, 0, 0],
    enabled: false,
  };
}

/** Пыль при ударе дербиса об пол. */
export function presetDustPuff(): EmitterConfig {
  return {
    visual: "smoke",
    blend: "alpha",
    rate: 0,
    lifetimeMin: 0.45,
    lifetimeMax: 1.05,
    speedMin: 0.35,
    speedMax: 1.25,
    coneAngleRad: 1.18,
    emitDir: [0, 0, 1],
    gravityZ: 0.15,
    drag: 0.55,
    turbulence: 0.9,
    baseSize: 0.28,
    sizeOverLife: [
      { t: 0, v: 0.4 },
      { t: 0.35, v: 1 },
      { t: 1, v: 1.35 },
    ],
    alphaOverLife: [
      { t: 0, v: 0 },
      { t: 0.06, v: 0.62 },
      { t: 0.55, v: 0.48 },
      { t: 1, v: 0 },
    ],
    offset: [0, 0, 0],
    enabled: false,
  };
}

/** Тлеющий дым после того как точка перестала получать попадания. */
export function presetAfterCoolingSmoke(): EmitterConfig {
  return {
    visual: "smoke",
    blend: "alpha",
    rate: 0,
    lifetimeMin: 2.2,
    lifetimeMax: 4.2,
    speedMin: 0.06,
    speedMax: 0.22,
    coneAngleRad: 1.0,
    emitDir: [0, 0, 1],
    gravityZ: 0.05,
    drag: 0.45,
    turbulence: 0.55,
    baseSize: 0.48,
    sizeOverLife: [
      { t: 0, v: 0.3 },
      { t: 0.4, v: 1 },
      { t: 1, v: 1.4 },
    ],
    alphaOverLife: [
      { t: 0, v: 0 },
      { t: 0.15, v: 0.38 },
      { t: 0.55, v: 0.32 },
      { t: 1, v: 0 },
    ],
    offset: [0, 0, 0],
    enabled: false,
  };
}

/** Угли / медленные искры над sustain-fire (desktop). */
export function presetEmbers(): EmitterConfig {
  return {
    visual: "spark",
    blend: "additive",
    rate: 0,
    lifetimeMin: 1.4,
    lifetimeMax: 2.8,
    speedMin: 0.35,
    speedMax: 1.1,
    coneAngleRad: 0.55,
    emitDir: [0, 0, 1],
    gravityZ: -0.35,
    drag: 0.35,
    turbulence: 0.25,
    baseSize: 0.055,
    sizeOverLife: [
      { t: 0, v: 0.6 },
      { t: 0.25, v: 1 },
      { t: 1, v: 0.4 },
    ],
    alphaOverLife: [
      { t: 0, v: 1 },
      { t: 0.85, v: 1 },
      { t: 1, v: 0 },
    ],
    offset: [0, 0, 0.04],
    enabled: false,
  };
}

/** Лёгкий дым в след за крупной падающей группой. */
export function presetFallingSmokeTrail(): EmitterConfig {
  return {
    visual: "smoke",
    blend: "alpha",
    rate: 0,
    lifetimeMin: 0.55,
    lifetimeMax: 1.35,
    speedMin: 0.08,
    speedMax: 0.42,
    coneAngleRad: 1.05,
    emitDir: [0, 0, 1],
    gravityZ: 0.12,
    drag: 0.7,
    turbulence: 0.55,
    baseSize: 0.42,
    sizeOverLife: [
      { t: 0, v: 0.35 },
      { t: 0.5, v: 1 },
      { t: 1, v: 1.5 },
    ],
    alphaOverLife: [
      { t: 0, v: 0 },
      { t: 0.12, v: 0.42 },
      { t: 0.65, v: 0.35 },
      { t: 1, v: 0 },
    ],
    offset: [0, 0, 0],
    enabled: false,
  };
}
